import React, { useState, useEffect } from 'react';
import { Map, Marker, ZoomControl } from 'pigeon-maps';
import { FaWhatsapp, FaExpand, FaCompress } from 'react-icons/fa';
import { BsInstagram } from 'react-icons/bs';

interface MapViewProps {
  shops: Array<{
    name: string;
    location: string;
    lat: number;
    lon: number;
    shopLogo?: string;
    offers: Array<any>;
    whatsapp?: string | null;
    instagram?: string | null;
  }>;
  userLocation: {
    latitude: number;
    longitude: number;
  } | null;
}

const MapView: React.FC<MapViewProps> = ({ shops, userLocation }) => {
  const [selectedShop, setSelectedShop] = useState<any | null>(null);
  const [validShops, setValidShops] = useState<any[]>([]);
  const [mapCenter, setMapCenter] = useState<[number, number]>([-23.305, -46.536]); // Default to Jacarei-SP
  const [zoom, setZoom] = useState<number>(13);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  
  // Debug shop data
  console.log('Shops data received in MapView:', shops);
  console.log('User location:', userLocation);
  
  useEffect(() => {
    if (shops && shops.length > 0) {
      // Filter shops with valid coordinates
      const filtered = shops.filter(shop => {
        const hasLat = typeof shop.lat !== 'undefined' && shop.lat !== null;
        const hasLon = typeof shop.lon !== 'undefined' && shop.lon !== null;
        
        if (!hasLat || !hasLon) {
          console.warn(`Shop ${shop.name} has invalid coordinates:`, shop.lat, shop.lon);
          return false;
        }
        
        // Convert to numbers if they're strings
        const lat = typeof shop.lat === 'string' ? parseFloat(shop.lat) : shop.lat;
        const lon = typeof shop.lon === 'string' ? parseFloat(shop.lon) : shop.lon;
        
        // Check if they're valid numbers
        if (isNaN(lat) || isNaN(lon)) {
          console.warn(`Shop ${shop.name} has NaN coordinates:`, shop.lat, shop.lon);
          return false;
        }
        
        // Check if they're in valid range
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
          console.warn(`Shop ${shop.name} has out-of-range coordinates:`, lat, lon);
          return false;
        }
        
        return true;
      });
      
      console.log(`Found ${filtered.length} shops with valid coordinates out of ${shops.length} total`);
      setValidShops(filtered);
    }
  }, [shops]);
  
  // Update map center when user location changes
  useEffect(() => {
    if (userLocation) {
      setMapCenter([userLocation.latitude, userLocation.longitude]);
      setZoom(14); // Zoom in when centering on user location
    }
  }, [userLocation]);
  
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm ${isFullscreen ? 'fixed inset-0 z-50 p-0' : 'p-6'}`}>
      {!isFullscreen && (
        <div className="text-center mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Mapa de Lojas</h2>
          <p className="text-gray-600">Visualize todas as lojas parceiras disponíveis no mapa</p>
        </div>
      )}
      
      <div className={`${isFullscreen ? 'h-screen' : 'h-[60vh]'} w-full rounded-lg overflow-hidden border border-gray-200 relative`}>
        <div className="absolute bottom-0 left-0 z-10 bg-white/80 text-xs px-1 py-0.5 text-gray-600">
          © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="hover:underline">OpenStreetMap</a> contributors
        </div>
        
        {/* Fullscreen toggle button */}
        <button 
          onClick={toggleFullscreen}
          className="absolute top-2 right-2 z-10 bg-white rounded-md p-2 shadow-md hover:bg-gray-100 transition-colors"
          aria-label={isFullscreen ? "Sair da tela cheia" : "Tela cheia"}
        >
          {isFullscreen ? <FaCompress size={20} /> : <FaExpand size={20} />}
        </button>
        <Map 
          height={isFullscreen ? window.innerHeight : 600}
          center={mapCenter} 
          zoom={zoom}
          attributionPrefix={false}
          onBoundsChanged={({ center, zoom }) => {
            setMapCenter(center);
            setZoom(zoom);
          }}
        >
          <ZoomControl />
          
          {/* User location marker */}
          {userLocation && (
            <Marker 
              width={40}
              anchor={[userLocation.latitude, userLocation.longitude]}
              color="#3b82f6"
              onClick={() => setSelectedShop(null)}
            />
          )}
          
          {/* Shop markers */}
          {validShops.length > 0 ? (
            validShops.map((shop) => {
              console.log('Rendering shop marker:', shop.name, shop.lat, shop.lon);
              // Convert string coordinates to numbers if needed
              const lat = typeof shop.lat === 'string' ? parseFloat(shop.lat) : shop.lat;
              const lon = typeof shop.lon === 'string' ? parseFloat(shop.lon) : shop.lon;
              
              return (
                <Marker 
                  key={`${shop.name}-${lat}-${lon}`}
                  width={40}
                  anchor={[lat, lon]}
                  color="#eb6813"
                  onClick={() => setSelectedShop(shop)}
                />
              );
            })
          ) : (
            // Don't render anything if no valid shops
            null
          )}
        </Map>
        
        {/* Shop info popup */}
        {selectedShop && (
          <div className="absolute bottom-4 left-0 right-0 mx-auto w-[90%] max-w-sm bg-white p-4 rounded-lg shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              {selectedShop.shopLogo ? (
                <img 
                  src={selectedShop.shopLogo} 
                  alt={selectedShop.name} 
                  className="w-12 h-12 rounded-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#eb6813]/10 flex items-center justify-center text-[#eb6813] font-medium">
                  {selectedShop.name.charAt(0)}
                </div>
              )}
              <div>
                <h3 className="font-medium text-gray-800">{selectedShop.name}</h3>
                <p className="text-sm text-gray-500">{selectedShop.location}</p>
              </div>
              <button 
                onClick={() => setSelectedShop(null)}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="text-sm mb-3">
              {selectedShop.offers.length > 0 ? (
                <div className="bg-green-50 px-2 py-1 rounded-md text-green-700 text-center">
                  {selectedShop.offers.length} oferta(s) disponível(is)
                </div>
              ) : (
                <div className="bg-gray-50 px-2 py-1 rounded-md text-gray-500 text-center">
                  Sem ofertas no momento
                </div>
              )}
            </div>
            
            <div className="flex justify-center gap-3">
              {selectedShop.whatsapp && (
                <a 
                  href={`https://wa.me/${selectedShop.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp size={16} />
                  <span>WhatsApp</span>
                </a>
              )}
              
              {selectedShop.instagram && (
                <a 
                  href={selectedShop.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-1.5 bg-pink-500 text-white rounded-md hover:bg-pink-600 transition-colors"
                >
                  <BsInstagram size={16} />
                  <span>Instagram</span>
                </a>
              )}
            </div>
          </div>
        )}
      </div>
      
      {!isFullscreen && (
        <div className="mt-4 text-sm text-center text-gray-500">
          <p>Clique nos marcadores para ver detalhes das lojas</p>
          {!userLocation && (
            <p className="mt-2">
              <button 
                className="text-[#eb6813] hover:underline"
                onClick={() => {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      console.log("Obteve localização do usuário");
                    },
                    (error) => {
                      console.error("Erro ao obter localização:", error);
                    }
                  );
                }}
              >
                Ative sua localização para ver lojas próximas
              </button>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default MapView;
